import { useEffect, useMemo, useState } from "react";
import { auth, storage, db } from "../firebase";
import { updateProfile, sendEmailVerification } from "firebase/auth";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  doc,
  getDoc,
  setDoc,
  query,
  collection,
  where,
  getDocs,
  serverTimestamp,
  updateDoc,
  arrayUnion,
  arrayRemove,
  limit,
  onSnapshot,
} from "firebase/firestore";
import { HiOutlineCurrencyDollar, HiOutlineTrophy } from "react-icons/hi2";
import { getAffiliateBonusState, getWithdrawableBalance } from "../lib/affiliateBonus";
import {
  Medal,
  Clipboard,
  ShieldCheck,
  Phone,
  Plus,
  Trash2,
} from "lucide-react";

/* ───────────────── helpers ───────────────── */
const COUNTRIES = [
  { code: "LK", name: "Sri Lanka", dial: "+94", pattern: /^7\d{8}$/ }, // 9 digits starting with 7
  { code: "IN", name: "India", dial: "+91", pattern: /^[6-9]\d{9}$/ },
  { code: "US", name: "United States", dial: "+1", pattern: /^[2-9]\d{9}$/ },
  { code: "GB", name: "United Kingdom", dial: "+44", pattern: /^\d{10}$/ },
  { code: "EU", name: "Other", dial: "+", pattern: /^\d{6,15}$/ }, // fallback
];

function sanitizeDigits(s = "") {
  return (s || "").replace(/\D/g, "");
}
function fmtCurrency(n = 0) {
  try {
    return Number(n).toLocaleString(undefined, { minimumFractionDigits: 2 });
  } catch {
    return String(n);
  }
}

/* ───────────────── inline: User bank manager ───────────────── */
function UserBankManager({ user }) {
  const [profile, setProfile] = useState(null);
  const [bank, setBank] = useState({
    bankName: "",
    accountName: "",
    accountNumber: "",
    branch: "",
  });

  useEffect(() => {
    if (!user?.uid) return;
    const refUser = doc(db, "users", user.uid);

    // live user doc so the payments tab stays after first add
    const unsub = onSnapshot(refUser, async (snap) => {
      if (snap.exists()) {
        setProfile({ id: snap.id, ...snap.data() });
      } else {
        await setDoc(
          refUser,
          { createdAt: serverTimestamp(), banks: [] },
          { merge: true }
        );
        const s2 = await getDoc(refUser);
        setProfile({ id: s2.id, ...s2.data() });
      }
    });
    return () => unsub();
  }, [user?.uid]);

  const addBank = async () => {
    const clean = {
      bankName: bank.bankName.trim(),
      accountName: bank.accountName.trim(),
      accountNumber: bank.accountNumber.trim(),
      branch: bank.branch.trim(),
      _id: `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    };
    if (!clean.bankName || !clean.accountName || !clean.accountNumber) {
      return toast.error("Bank name, account name & number are required");
    }
    try {
      await updateDoc(doc(db, "users", user.uid), {
        banks: arrayUnion(clean),
        updatedAt: serverTimestamp(),
      });
      setBank({ bankName: "", accountName: "", accountNumber: "", branch: "" });
      toast.success("Bank added");
    } catch (e) {
      console.error(e);
      toast.error("Failed to add bank");
    }
  };

  const removeBank = async (b) => {
    try {
      await updateDoc(doc(db, "users", user.uid), { banks: arrayRemove(b) });
      toast.success("Removed");
    } catch (e) {
      console.error(e);
      toast.error("Failed to remove");
    }
  };

  return (
    <div className="max-w-3xl mx-auto mt-6">
      <h2 className="text-2xl font-bold mb-4 text-white">Payment Methods</h2>
      <p className="text-slate-300 mb-3 text-sm">
        Add a bank account you want to use for agent payments, refunds, or
        withdrawals.
      </p>

      <div className="p-4 rounded-2xl bg-slate-800">
        <div className="grid md:grid-cols-2 gap-3">
          <label className="block">
            <div className="text-sm text-slate-300">Bank Name</div>
            <input
              className="w-full mt-1 px-3 py-2 rounded bg-slate-700 text-white"
              value={bank.bankName}
              onChange={(e) =>
                setBank((s) => ({ ...s, bankName: e.target.value }))
              }
            />
          </label>
          <label className="block">
            <div className="text-sm text-slate-300">Account Name</div>
            <input
              className="w-full mt-1 px-3 py-2 rounded bg-slate-700 text-white"
              value={bank.accountName}
              onChange={(e) =>
                setBank((s) => ({ ...s, accountName: e.target.value }))
              }
            />
          </label>
          <label className="block">
            <div className="text-sm text-slate-300">Account Number</div>
            <input
              className="w-full mt-1 px-3 py-2 rounded bg-slate-700 text-white"
              value={bank.accountNumber}
              onChange={(e) =>
                setBank((s) => ({ ...s, accountNumber: e.target.value }))
              }
            />
          </label>
          <label className="block">
            <div className="text-sm text-slate-300">Branch (optional)</div>
            <input
              className="w-full mt-1 px-3 py-2 rounded bg-slate-700 text-white"
              value={bank.branch}
              onChange={(e) =>
                setBank((s) => ({ ...s, branch: e.target.value }))
              }
            />
          </label>
        </div>
        <button
          onClick={addBank}
          className="mt-3 px-3 py-2 rounded bg-indigo-400 text-black font-semibold inline-flex items-center gap-2"
        >
          <Plus size={16} /> Add Bank
        </button>

        <div className="mt-4 space-y-2">
          {(profile?.banks || []).map((b) => (
            <div
              key={b._id}
              className="p-3 bg-slate-700 rounded flex items-center justify-between"
            >
              <div className="text-sm text-white">
                <div className="font-semibold">{b.bankName}</div>
                <div className="text-slate-300">
                  {b.accountName} · {b.accountNumber}
                  {b.branch ? ` · ${b.branch}` : ""}
                </div>
              </div>
              <button
                onClick={() => removeBank(b)}
                className="p-2 rounded bg-slate-600 text-white hover:bg-slate-500"
                title="Remove"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
          {!(profile?.banks || []).length && (
            <div className="text-slate-400 text-sm">No banks yet.</div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ───────────────── main component ───────────────── */
export default function Profile({ user }) {
  const navigate = useNavigate();

  // profile
  const [name, setName] = useState("");
  const [photo, setPhoto] = useState("");
  const [uploading, setUploading] = useState(false);
  const [verifying, setVerifying] = useState(false);

  // stats / user doc
  const [userDoc, setUserDoc] = useState(null);
  const [affiliateSummary, setAffiliateSummary] = useState({ links: [], referralCount: 0 });
  const [creatingAffiliateLink, setCreatingAffiliateLink] = useState(false);

  // phone
  const [phoneCountry, setPhoneCountry] = useState("LK");
  const [phoneLocal, setPhoneLocal] = useState(""); // digits only
  const [savingPhone, setSavingPhone] = useState(false);

  // tabs + eligibility
  const [tab, setTab] = useState("profile"); // 'profile' | 'phone' | 'payments'
  const [hasTicket, setHasTicket] = useState(false);

  useEffect(() => {
    if (user === null) navigate("/login");
  }, [user, navigate]);

  // live user doc (to know banks, phone, etc.)
  useEffect(() => {
    if (!user?.uid) return;
    const refUser = doc(db, "users", user.uid);
    const unsub = onSnapshot(refUser, (snap) => {
      if (snap.exists()) {
        const d = snap.data();
        setUserDoc(d);
        if (d.phoneCountry) setPhoneCountry(d.phoneCountry);
        if (d.phone) setPhoneLocal(sanitizeDigits(d.phone));
      } else {
        setUserDoc(null);
      }
    });
    return () => unsub();
  }, [user?.uid]);

  // mirror auth profile to local UI
  useEffect(() => {
    if (user) {
      setName(user.displayName || "");
      setPhoto(user.photoURL || "");
    }
  }, [user]);

  useEffect(() => {
    let cancelled = false;

    async function loadAffiliateSummary() {
      if (!user?.uid) {
        setAffiliateSummary({ links: [], referralCount: 0 });
        return;
      }

      try {
        const [linksSnap, referralsSnap] = await Promise.all([
          getDocs(query(collection(db, "affiliate_links"), where("userId", "==", user.uid))),
          getDocs(query(collection(db, "users"), where("affiliateId", "==", user.uid))),
        ]);

        if (cancelled) return;
        setAffiliateSummary({
          links: linksSnap.docs.map((entry) => ({ id: entry.id, ...entry.data() })),
          referralCount: referralsSnap.size,
        });
      } catch (error) {
        if (!cancelled) {
          setAffiliateSummary({ links: [], referralCount: 0 });
        }
      }
    }

    loadAffiliateSummary();
    return () => {
      cancelled = true;
    };
  }, [user?.uid]);

  // 🔥 has ever bought? — match your My Tickets logic (uid + phone variants)
  useEffect(() => {
    if (!user?.uid) return;

    // reset and attach multiple listeners
    setHasTicket(false);
    const unsubs = [];
    const bump = (snap) => {
      if (!snap.empty) setHasTicket(true);
    };

    // by uid
    unsubs.push(
      onSnapshot(
        query(
          collection(db, "tickets"),
          where("userUid", "==", user.uid),
          limit(1)
        ),
        bump
      )
    );

    // by phones (same shapes used in your tickets page)
    const phoneE164 = userDoc?.phoneE164 || null;
    const digits = (userDoc?.phone || userDoc?.mobile || "").replace(/\D/g, "");

    if (phoneE164) {
      unsubs.push(
        onSnapshot(
          query(
            collection(db, "tickets"),
            where("userMobileE164", "==", phoneE164),
            limit(1)
          ),
          bump
        )
      );
      // some docs store E164 inside userMobile
      unsubs.push(
        onSnapshot(
          query(
            collection(db, "tickets"),
            where("userMobile", "==", phoneE164),
            limit(1)
          ),
          bump
        )
      );
    }
    if (digits) {
      unsubs.push(
        onSnapshot(
          query(
            collection(db, "tickets"),
            where("userMobile", "==", digits),
            limit(1)
          ),
          bump
        )
      );
    }

    return () => unsubs.forEach((u) => u && u());
  }, [user?.uid, userDoc?.phoneE164, userDoc?.phone, userDoc?.mobile]);

  /* ───────── level/medal visuals ───────── */
  function getLevel(iq = 0) {
    if (iq >= 1200) return 8;
    if (iq >= 800) return 7;
    if (iq >= 500) return 6;
    if (iq >= 300) return 5;
    if (iq >= 100) return 4;
    if (iq >= 50) return 3;
    if (iq >= 20) return 2;
    return 1;
  }
  function getLevelName(level) {
    return (
      [
        "",
        "Rookie",
        "Novice",
        "Intermediate",
        "Skilled",
        "Pro",
        "Elite",
        "Master",
        "Legend",
      ][level] || "Rookie"
    );
  }

  const overallIQ = userDoc?.overall_iq ?? 0;
  const level = getLevel(overallIQ);
  const nextLevelXP = [0, 20, 50, 100, 300, 500, 800, 1200, 2000][level] || 0;
  const prevLevelXP = [0, 0, 20, 50, 100, 300, 500, 800, 1200][level] || 0;
  const progressPercent =
    nextLevelXP > prevLevelXP
      ? ((overallIQ - prevLevelXP) / (nextLevelXP - prevLevelXP)) * 100
      : 0;

  /* ───────── actions ───────── */
  const handleSaveProfile = async () => {
    try {
      await updateProfile(auth.currentUser, {
        displayName: name,
        photoURL: photo,
      });
      await setDoc(
        doc(db, "users", user.uid),
        {
          displayName: name,
          avatar: photo || "/default-avatar.png",
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      toast.success("Profile updated");
    } catch {
      toast.error("Failed to update profile");
    }
  };

  const handleEmailVerification = async () => {
    try {
      setVerifying(true);
      await sendEmailVerification(auth.currentUser);
      toast.success("Verification email sent to " + user.email);
    } catch {
      toast.error("Failed to send verification email");
    } finally {
      setVerifying(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const storageRef = ref(storage, `avatars/${user.uid}`);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      setPhoto(downloadURL);
      toast.success("Image uploaded");
    } catch {
      toast.error("Image upload failed");
    } finally {
      setUploading(false);
    }
  };

  /* ───────── phone save with uniqueness check ───────── */
  const dialPrefix = useMemo(
    () => COUNTRIES.find((c) => c.code === phoneCountry)?.dial || "+",
    [phoneCountry]
  );

  const validatePhone = () => {
    const rule = COUNTRIES.find((c) => c.code === phoneCountry)?.pattern;
    const digits = sanitizeDigits(phoneLocal);
    if (!digits) return "Enter your phone number";
    if (rule && !rule.test(digits)) return "Invalid number format for region";
    const e164 = `${dialPrefix.replace(/\+$/, "+")}${digits}`;
    if (!/^\+\d{6,15}$/.test(e164)) return "Invalid full phone number";
    return null;
  };

  const handleCreateAffiliateLink = async () => {
    if (!user?.uid) return toast.error("Login first");
    if (affiliateSummary.links.length) {
      const existingLink = affiliateSummary.links[0]?.referralLink;
      if (existingLink) {
        navigator.clipboard.writeText(existingLink);
        toast.success("Affiliate link copied!");
      }
      return;
    }

    try {
      setCreatingAffiliateLink(true);
      const refLink = doc(collection(db, "affiliate_links"));
      const baseUrl =
        typeof window !== "undefined"
          ? window.location.origin
          : "https://www.iqcage.com";
      const nextLink = {
        id: refLink.id,
        userId: user.uid,
        linkType: "Register link",
        program: "Turnover Sharing",
        comment: "Profile default link",
        createdAt: new Date(),
        referralLink: `${baseUrl}/register?lid=${refLink.id}`,
      };

      await setDoc(refLink, {
        userId: user.uid,
        linkType: nextLink.linkType,
        program: nextLink.program,
        comment: nextLink.comment,
        createdAt: serverTimestamp(),
        referralLink: nextLink.referralLink,
      });

      setAffiliateSummary((current) => ({
        ...current,
        links: [nextLink, ...current.links],
      }));
      navigator.clipboard.writeText(nextLink.referralLink);
      toast.success("Affiliate link created and copied!");
    } catch (error) {
      console.error(error);
      toast.error("Failed to create affiliate link");
    } finally {
      setCreatingAffiliateLink(false);
    }
  };

  const handleSavePhone = async () => {
    if (!user?.uid) return toast.error("Login first");
    const err = validatePhone();
    if (err) return toast.error(err);

    const digits = sanitizeDigits(phoneLocal);
    const e164 = `${dialPrefix.replace(/\+$/, "+")}${digits}`;

    try {
      setSavingPhone(true);

      // uniqueness
      const qy = query(collection(db, "users"), where("phoneE164", "==", e164));
      const snap = await getDocs(qy);
      const takenByOther = snap.docs.some((d) => d.id !== user.uid);
      if (takenByOther) {
        toast.error("This mobile number is already used by another account.");
        return;
      }

      await setDoc(
        doc(db, "users", user.uid),
        {
          phone: digits,
          phoneCountry,
          phoneE164: e164,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      toast.success("Mobile number updated");
    } catch (e) {
      console.error(e);
      toast.error("Failed to save number");
    } finally {
      setSavingPhone(false);
    }
  };

  /* ───────── UI ───────── */
  if (!user) return <div className="p-6">Loading…</div>;

  const stats = [
    {
      label: "Total Wins",
      value: userDoc?.totalWins || 0,
      icon: <HiOutlineTrophy className="text-yellow-400 w-6 h-6" />,
    },
    {
      label: "Total Bets",
      value: userDoc?.totalBets || 0,
      icon: <Medal className="text-emerald-400 w-6 h-6" />,
    },
    {
      label: "Total Wagered",
      value: "$" + fmtCurrency(userDoc?.totalWagered || 0),
      icon: <HiOutlineCurrencyDollar className="text-blue-400 w-6 h-6" />,
    },
  ];

  const medals = userDoc?.medals || [
    { name: "Early Bird", color: "bg-orange-400" },
    { name: "First Win", color: "bg-purple-500" },
  ];

  const emailProvider = user.providerData?.[0]?.providerId;
  const affiliateBonusState = getAffiliateBonusState(userDoc || {});
  const withdrawableBalance = getWithdrawableBalance(userDoc || {});
  const primaryAffiliateLink = affiliateSummary.links[0]?.referralLink || "";

  const hasBanks = (userDoc?.banks?.length || 0) > 0;
  const canShowPayments = hasTicket || hasBanks; // once true, tab stays

  return (
    <div className="max-w-5xl mx-auto p-6 mt-10 md:mt-16 space-y-8">
      {/* Header card */}
      <div className="rounded-3xl bg-gradient-to-br from-slate-900 to-indigo-900 p-6 shadow-2xl">
        <div className="flex flex-col md:flex-row md:items-center gap-6">
          <div className="relative">
            <img
              src={photo || "/default-avatar.png"}
              alt="Profile"
              className="w-28 h-28 rounded-full object-cover ring-4 ring-emerald-400/30 shadow-xl"
            />
            <span className="absolute -bottom-3 left-1/2 -translate-x-1/2 px-3 py-1 text-xs rounded-full bg-gradient-to-r from-emerald-500 to-blue-500 text-white font-bold shadow-md">
              🏅 {getLevelName(level)} (Lv.{level})
            </span>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div className="min-w-0">
                <div className="text-2xl font-extrabold text-white truncate">
                  {name || "Unnamed Player"}
                </div>
                <div className="mt-1 text-xs text-gray-300 flex items-center gap-2">
                  UID:
                  <span className="font-mono bg-slate-800/80 px-2 py-0.5 rounded">
                    {user.uid}
                  </span>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(user.uid);
                      toast.success("User ID copied!");
                    }}
                    className="text-lime-400 hover:text-lime-300 transition"
                  >
                    <Clipboard className="w-4 h-4" />
                  </button>
                </div>
                <div className="mt-2 w-56 h-2 bg-slate-800/60 rounded-full overflow-hidden">
                  <div
                    className="h-2 bg-lime-400 transition-all"
                    style={{
                      width: `${Math.max(0, Math.min(100, progressPercent))}%`,
                    }}
                  />
                </div>
                <div className="text-xs text-gray-300 mt-1">
                  XP: <b>{overallIQ}</b> / {nextLevelXP}
                </div>
              </div>

              <div className="self-start md:self-auto text-sm text-lime-300 font-medium flex items-center gap-2">
                {user.email}
                {user.emailVerified ? (
                  <span className="px-2 py-0.5 text-xs bg-green-600 rounded-full font-bold text-white">
                    ✅ Verified
                  </span>
                ) : (
                  <>
                    <span className="px-2 py-0.5 text-xs bg-yellow-500 rounded-full font-bold text-white">
                      ⚠️ Unverified
                    </span>
                    {emailProvider !== "google.com" && (
                      <button
                        onClick={handleEmailVerification}
                        disabled={verifying}
                        className="ml-3 px-3 py-1 text-xs bg-blue-500 hover:bg-blue-600 rounded-full font-bold text-white transition"
                      >
                        {verifying ? "Sending…" : "Verify Email"}
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Tabs */}
            <div className="mt-3 flex gap-2">
              <button
                onClick={() => setTab("profile")}
                className={`px-3 py-1.5 rounded-full text-sm ${
                  tab === "profile"
                    ? "bg-lime-400 text-black"
                    : "bg-slate-700 text-white"
                }`}
              >
                Profile
              </button>
              <button
                onClick={() => setTab("phone")}
                className={`px-3 py-1.5 rounded-full text-sm ${
                  tab === "phone"
                    ? "bg-lime-400 text-black"
                    : "bg-slate-700 text-white"
                }`}
              >
                Mobile
              </button>
              {canShowPayments && (
                <button
                  onClick={() => setTab("payments")}
                  className={`px-3 py-1.5 rounded-full text-sm ${
                    tab === "payments"
                      ? "bg-lime-400 text-black"
                      : "bg-slate-700 text-white"
                  }`}
                >
                  Payment Methods
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {stats.map((s) => (
          <div
            key={s.label}
            className="bg-slate-800 rounded-2xl shadow p-5 flex items-center gap-3"
          >
            <div>{s.icon}</div>
            <div>
              <div className="text-xl font-bold text-white">{s.value}</div>
              <div className="text-xs text-gray-400">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-2xl bg-slate-800 p-5">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <div className="font-bold text-white mb-1">Affiliate</div>
            <div className="text-sm text-slate-400 max-w-2xl">
              Share your affiliate link, track referred users, and watch locked rewards unlock after turnover.
            </div>
          </div>
          <div className="rounded-full bg-emerald-500/15 px-4 py-2 text-sm font-bold text-emerald-300">
            Referred users: {affiliateSummary.referralCount}
          </div>
        </div>

        <div className="mt-4 grid gap-4 lg:grid-cols-[1.4fr_0.6fr]">
          <div className="rounded-2xl border border-white/10 bg-slate-900/40 p-4">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Affiliate link</div>
            {primaryAffiliateLink ? (
              <>
                <div className="mt-3 break-all rounded-xl bg-slate-950/60 px-4 py-3 font-mono text-sm text-white">
                  {primaryAffiliateLink}
                </div>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(primaryAffiliateLink);
                    toast.success("Affiliate link copied!");
                  }}
                  className="mt-3 rounded-full bg-lime-400 px-4 py-2 text-sm font-bold text-black"
                >
                  Copy link
                </button>
              </>
            ) : (
              <div className="mt-3">
                <div className="text-sm text-slate-400">
                  No affiliate link created yet. Create it here and copy it straight from your profile.
                </div>
                <button
                  onClick={handleCreateAffiliateLink}
                  disabled={creatingAffiliateLink}
                  className="mt-3 rounded-full bg-lime-400 px-4 py-2 text-sm font-bold text-black disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {creatingAffiliateLink ? "Creating link..." : "Create affiliate link"}
                </button>
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-white/10 bg-slate-900/40 p-4 space-y-2">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Rewards</div>
            <div className="text-sm text-slate-300">Locked bonus: <span className="font-bold text-amber-300">{affiliateBonusState.locked.toFixed(2)} USDT</span></div>
            <div className="text-sm text-slate-300">Unlocked bonus: <span className="font-bold text-emerald-300">{affiliateBonusState.unlocked.toFixed(2)} USDT</span></div>
            <div className="text-sm text-slate-300">Withdrawable: <span className="font-bold text-white">{withdrawableBalance.toFixed(2)} USDT</span></div>
            {affiliateBonusState.required > 0 ? (
              <div className="text-xs text-slate-400">
                Turnover progress: {affiliateBonusState.completed.toFixed(0)} / {affiliateBonusState.required.toFixed(0)}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Achievements */}
      <div className="rounded-2xl bg-slate-800 p-5">
        <div className="font-bold text-white mb-2">Achievements</div>
        <div className="flex gap-2 flex-wrap">
          {medals.map((m, i) => (
            <span
              key={i}
              className={`px-3 py-1 rounded-full text-xs font-semibold text-white ${m.color} shadow-md`}
            >
              {m.name}
            </span>
          ))}
        </div>
      </div>

      {/* Editors / Payments */}
      {tab === "profile" && (
        <div className="p-5 bg-slate-800 rounded-2xl shadow space-y-4">
          <div className="flex items-center gap-2 text-white font-semibold">
            <ShieldCheck className="w-5 h-5 text-emerald-400" />
            Profile
          </div>
          <label className="block text-sm font-bold text-gray-100">
            Display Name
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full p-2 rounded bg-slate-700 text-white"
          />
          <label className="block text-sm font-bold text-gray-100">
            Profile Picture
          </label>
          <div className="flex items-center gap-4">
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="block text-sm text-gray-300 file:mr-4 file:py-1 file:px-4 file:rounded-full file:bg-lime-200 hover:file:bg-lime-300"
            />
            {uploading && (
              <span className="text-sm text-gray-400">Uploading…</span>
            )}
            {photo && (
              <img
                src={photo}
                alt="Preview"
                className="w-12 h-12 rounded-full object-cover border shadow"
              />
            )}
          </div>
          <button
            onClick={handleSaveProfile}
            className="w-full mt-2 bg-lime-500 hover:bg-lime-600 text-black font-bold py-2 px-4 rounded shadow"
          >
            Save Profile
          </button>
        </div>
      )}

      {tab === "phone" && (
        <div className="p-5 bg-slate-800 rounded-2xl shadow space-y-4">
          <div className="flex items-center gap-2 text-white font-semibold">
            <Phone className="w-5 h-5 text-indigo-400" />
            Mobile Number
          </div>

          <div className="grid grid-cols-3 gap-2">
            <select
              className="col-span-1 px-3 py-2 rounded bg-slate-700 text-white"
              value={phoneCountry}
              onChange={(e) => setPhoneCountry(e.target.value)}
            >
              {COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name} ({c.dial})
                </option>
              ))}
            </select>
            <input
              className="col-span-2 px-3 py-2 rounded bg-slate-700 text-white"
              placeholder="Phone number (digits only)"
              value={phoneLocal}
              onChange={(e) => setPhoneLocal(sanitizeDigits(e.target.value))}
            />
          </div>

          <div className="text-xs text-slate-400">
            We’ll store your number as{" "}
            <span className="font-mono">
              {COUNTRIES.find((c) => c.code === phoneCountry)?.dial}
              {sanitizeDigits(phoneLocal)}
            </span>
            . This must be unique to your account.
          </div>

          <button
            onClick={handleSavePhone}
            disabled={savingPhone}
            className="w-full mt-1 bg-indigo-400 hover:bg-indigo-500 text-black font-bold py-2 px-4 rounded shadow disabled:opacity-60"
          >
            {savingPhone ? "Saving…" : "Save Mobile Number"}
          </button>
        </div>
      )}

      {tab === "payments" && <UserBankManager user={user} />}
    </div>
  );
}
